import { AclService } from 'phoenix/services/aclService';
import { 
	Acl, 
	ShareBaseItem,
	AclPrincipal,
	ReceivedShareInfo,
	PermissionType,
	pxItemTypeGuards
} from 'phoenix/domain';
import { IDictionary } from 'util/domain';
export interface AclDatastore {
	ownerAcl:Acl;
	load(item:ShareBaseItem):ng.IPromise<Acl>;
	loadShares(item:ShareBaseItem):ng.IPromise<Acl[]>;
	clearCache():void;
	hasPermission(item:any, perm:PermissionType): ng.IPromise<boolean>;
	addToSharesCaches(item:ShareBaseItem, newShares:Acl | Acl[]):ng.IPromise<Acl[]>;
	removeFromSharesCaches(item:ShareBaseItem, removedShare:Acl):ng.IPromise<Acl[]>;
	clearShareCache(item:ShareBaseItem):void;
}

/**
* @ngdoc service
* @name  sharebase-app.sbAclDatastore
* 
* @description 
* A datastore used to load and cache ACLs
* 
**/
export function AclDatastore(
	$http:ng.IHttpService, 
	$q:ng.IQService,
	pxAclService:AclService
) {
	let _ownerAcl:Acl;
	let _aclCache:IDictionary<ng.IPromise<Acl[]>> = {};
	let _sharesCache:IDictionary<ng.IPromise<Acl[]>> = {};
	let datastore:AclDatastore = {
		/**
		* @ngdoc method
		* @name sbAclDatastore#load
		* @methodOf sharebase-app.sbAclDatastore
		* @kind function
		*
		* @description
		* Loads or refreshes the user's ACL for an item
		*
		* @param {item} item Item of the current user's permission ACL to load
		*
		* @returns {Deferred} A promise that will resolve with
		* the user's current permissions
		*/
		load,
		loadShares,
		get ownerAcl(){ return _ownerAcl; },
		/**
		* @ngdoc method
		* @name sbAclDatastore#clearCache
		* @methodOf sharebase-app.sbAclDatastore
		* @kind function
		*
		* @description
		* Clears the datastore's cache of ACLs
		*/
		clearCache,
		hasPermission,
		/**
		 * @ngdoc method
		 * @name sbAclDatastore#addToSharesCaches
		 * @methodOf sharebase-app.sbAclDatastore
		 * @kind function
		 * 
		 * @description
		 * Updates the shares cache with new or removed shares
		 * 
		 * @param {item} item The item whose shares have changed
		 * 
		 * @param {item} newShares A collection of ACL promises to cache
		 * 
		 * @return {Deferred} A promise that will resolve with the pushed changes. 
		 */
		addToSharesCaches,
		removeFromSharesCaches,
		clearShareCache
	};

	return datastore;

	function load(item:ShareBaseItem){
		return _load<AclPrincipal>(item, 'permissionsforuser', _aclCache).then(perms => pxAclService.generateEffectiveRights(...perms));
	}

	function loadShares(item:ShareBaseItem){ 
		return _load<ReceivedShareInfo>(item, 'permissions', _sharesCache).then(shares => {
			let _shares:Acl[] = [];
			shares.forEach(share => {
				let source = share.source as ReceivedShareInfo;
				if(source.RoleName === 'Owner'){
					_ownerAcl = share;
				}
				else {
					_shares.push(share);
				}
			});
	
			return _shares;
		});
	}

	function getCacheKey(item:ShareBaseItem) {
		let cacheKey:string;

		if(pxItemTypeGuards.isShareBaseDocument(item)) {
			cacheKey = 'document' + item.DocumentId;
		}
		else if(pxItemTypeGuards.isShareBaseFolder(item)) {
			cacheKey = 'folder' + item.FolderId;
		}
		else if(pxItemTypeGuards.isShareBaseLibrary(item)){
			cacheKey = 'library' + item.LibraryId;
		}

		return cacheKey;
	}

	function _load<T extends AclPrincipal>(item:ShareBaseItem, permsEndpoint, cache:IDictionary<ng.IPromise<Acl[]>>, filterPredicate:(share:T) => boolean = () => true) {
		let cacheACLProp = getCacheKey(item);		

		// check the cache of ACLs
		if (cache[cacheACLProp]) {
			return cache[cacheACLProp];
		}

		// not in cache so go get it
		let itemPermissionsUrl = `${item.Links.Self || item.Links.SelfUri}/${permsEndpoint}`;
		let aclPromise:ng.IPromise<Acl[]> = $http.get(itemPermissionsUrl)
			.then(response => {
				return (response.data as T[])
					.filter(filterPredicate)
					.map(data => pxAclService.create(data))
			})
			.catch(function(err) {
				// If the request errors, delete the cached promise
				// then return an error promise with the original error
				// This will prevent any false positives the request was
				// not successful.
				cache[cacheACLProp] = null;
				delete cache[cacheACLProp];

				return $q.reject(err);
			});
		
		// cache the promise so it can be used by subsequent requests then return it
		cache[cacheACLProp] = aclPromise;
		return aclPromise;
	}

	function addToSharesCaches(item:ShareBaseItem, updatedShares:Acl | Acl[]) {
		let cacheACLProp = getCacheKey(item);
		if (!Array.isArray(updatedShares)) {
			updatedShares = [updatedShares];
		}
		
		if (_sharesCache[cacheACLProp]) {
			_sharesCache[cacheACLProp].then(acls => {
				let oldShares = acls.filter(acl => {
					let hasMatch = false;
					for (let i = 0; i < (updatedShares as Acl[]).length; i++) {
						if (acl.elementId === updatedShares[i].elementId) {
							hasMatch = true;
							break;
						}
					}
					return !hasMatch;
				});

				_sharesCache[cacheACLProp] = $q.resolve(oldShares.concat(updatedShares));
			});
		}
		return _sharesCache[cacheACLProp];
	}

	function removeFromSharesCaches(item:ShareBaseItem, removedShare:Acl) {
		let cacheACLProp = getCacheKey(item);
		if (_sharesCache[cacheACLProp]) {
			let newCache = _sharesCache[cacheACLProp].then(acls => acls.filter(acl => acl.elementId !== removedShare.elementId));
			_sharesCache[cacheACLProp] = $q.resolve(newCache);
		}
		return _sharesCache[cacheACLProp];
	}

	function hasPermission(item:any, permission:PermissionType){
		let p = $q.resolve(false);
		if(item){
			p = datastore
				.load(item)
				.then(acl => acl.hasPermission(permission));
		}

		return p;
	}

	function clearCache() {
		_aclCache = {};
		_sharesCache = {};
		_ownerAcl = undefined;
	}

	function clearShareCache(item:ShareBaseItem) {
		let cacheACLProp = getCacheKey(item);
		_sharesCache[cacheACLProp] = null;
	}
}

AclDatastore.$inject =  [
	'$http',
	'$q',
	'pxAclService'
];


// WEBPACK FOOTER //
// ./src/sharebase/dataStores/aclDatastore.ts